import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid2 as Grid, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, IconButton, Switch,
  FormControlLabel, Alert, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip,
} from '@mui/material';
import {
  Add, Edit, Delete, Refresh, ArrowUpward, ArrowDownward, Image as ImageIcon,
  Visibility, VisibilityOff,
} from '@mui/icons-material';
import api from '../../services/api';

interface Banner {
  id: number;
  titulo: string;
  subtitulo: string;
  imagem: string | null;
  mimeType: string;
  link: string;
  acao: string;
  acaoId: string;
  corFundo: string;
  estado: string;
  ordem: number;
  dataInicio: string | null;
  dataFim: string | null;
  dataCriacao: string;
}

const GRADIENT_PRESETS = [
  { label: 'Laranja/Amarelo', value: 'linear-gradient(135deg, #FF6B35, #F7C948)' },
  { label: 'Roxo Pagali', value: 'linear-gradient(135deg, #6233A0, #9C27B0)' },
  { label: 'Azul', value: 'linear-gradient(135deg, #1976D2, #42A5F5)' },
  { label: 'Verde', value: 'linear-gradient(135deg, #2E7D32, #66BB6A)' },
  { label: 'Vermelho', value: 'linear-gradient(135deg, #C62828, #EF5350)' },
  { label: 'Rosa', value: 'linear-gradient(135deg, #AD1457, #EC407A)' },
  { label: 'Escuro', value: 'linear-gradient(135deg, #212121, #424242)' },
];

const FoodBanners: React.FC = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState<Banner | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });
  const [form, setForm] = useState({
    titulo: '', subtitulo: '', link: '', acao: 'none', acaoId: '',
    corFundo: GRADIENT_PRESETS[0].value, estado: 'A', ordem: 0,
    dataInicio: '', dataFim: '', imagem: '' as string | null, mimeType: 'image/png',
  });

  const fetchBanners = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/food/banners');
      setBanners(res.data.data || []);
    } catch { setBanners([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchBanners(); }, [fetchBanners]);

  const handleOpen = (banner?: Banner) => {
    if (banner) {
      setEditing(banner);
      setForm({
        titulo: banner.titulo, subtitulo: banner.subtitulo || '', link: banner.link || '',
        acao: banner.acao || 'none', acaoId: banner.acaoId || '',
        corFundo: banner.corFundo || GRADIENT_PRESETS[0].value,
        estado: banner.estado, ordem: banner.ordem,
        dataInicio: banner.dataInicio?.slice(0, 16) || '',
        dataFim: banner.dataFim?.slice(0, 16) || '',
        imagem: null, mimeType: banner.mimeType || 'image/png',
      });
    } else {
      setEditing(null);
      setForm({
        titulo: '', subtitulo: '', link: '', acao: 'none', acaoId: '',
        corFundo: GRADIENT_PRESETS[0].value, estado: 'A', ordem: banners.length,
        dataInicio: '', dataFim: '', imagem: null, mimeType: 'image/png',
      });
    }
    setDialog(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setForm({ ...form, imagem: reader.result as string, mimeType: file.type });
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.titulo.trim()) { setToast({ open: true, msg: 'Título é obrigatório', sev: 'error' }); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        dataInicio: form.dataInicio || null,
        dataFim: form.dataFim || null,
      };
      if (editing) {
        await api.put(`/admin/food/banners/${editing.id}`, payload);
        setToast({ open: true, msg: 'Banner actualizado', sev: 'success' });
      } else {
        await api.post('/admin/food/banners', payload);
        setToast({ open: true, msg: 'Banner criado', sev: 'success' });
      }
      setDialog(false);
      fetchBanners();
    } catch (err: any) {
      setToast({ open: true, msg: err.response?.data?.error || 'Erro', sev: 'error' });
    }
    setSaving(false);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Eliminar este banner?')) return;
    try {
      await api.delete(`/admin/food/banners/${id}`);
      setToast({ open: true, msg: 'Banner eliminado', sev: 'success' });
      fetchBanners();
    } catch { setToast({ open: true, msg: 'Erro ao eliminar', sev: 'error' }); }
  };

  const handleToggle = async (banner: Banner) => {
    try {
      await api.put(`/admin/food/banners/${banner.id}`, { estado: banner.estado === 'A' ? 'I' : 'A' });
      fetchBanners();
    } catch { /* ignore */ }
  };

  const handleReorder = async (id: number, direction: 'up' | 'down') => {
    const idx = banners.findIndex(b => b.id === id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= banners.length) return;
    try {
      await api.put(`/admin/food/banners/${banners[idx].id}`, { ordem: banners[swapIdx].ordem });
      await api.put(`/admin/food/banners/${banners[swapIdx].id}`, { ordem: banners[idx].ordem });
      fetchBanners();
    } catch { /* ignore */ }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Banners Promocionais</Typography>
          <Typography variant="body2" color="text.secondary">Gerir carrossel de promoções do Pagali Food</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchBanners}>Actualizar</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>Novo Banner</Button>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 1.5 }}>
            <Typography variant="h5" fontWeight={700} color="primary">{banners.length}</Typography>
            <Typography variant="caption" color="text.secondary">Total</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 1.5 }}>
            <Typography variant="h5" fontWeight={700} color="success.main">{banners.filter(b => b.estado === 'A').length}</Typography>
            <Typography variant="caption" color="text.secondary">Activos</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* Preview */}
      {banners.filter(b => b.estado === 'A').length > 0 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Pré-visualização</Typography>
          <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
            {banners.filter(b => b.estado === 'A').map(b => (
              <Box key={b.id} sx={{
                minWidth: 280, height: 140, borderRadius: 3, overflow: 'hidden', position: 'relative', flexShrink: 0,
                background: b.imagem ? undefined : b.corFundo,
              }}>
                {b.imagem && <img src={b.imagem} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.6), transparent)', p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                  <Typography variant="subtitle1" fontWeight={700} color="white">{b.titulo}</Typography>
                  {b.subtitulo && <Typography variant="caption" color="white">{b.subtitulo}</Typography>}
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={60}>Ordem</TableCell>
              <TableCell width={80}>Imagem</TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Subtítulo</TableCell>
              <TableCell width={100}>Estado</TableCell>
              <TableCell width={150}>Período</TableCell>
              <TableCell width={140} align="center">Acções</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>A carregar...</TableCell></TableRow>
            ) : banners.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>Nenhum banner. Clique "Novo Banner" para criar.</TableCell></TableRow>
            ) : banners.map((b, idx) => (
              <TableRow key={b.id} hover sx={{ opacity: b.estado === 'A' ? 1 : 0.5 }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton size="small" disabled={idx === 0} onClick={() => handleReorder(b.id, 'up')}><ArrowUpward fontSize="small" /></IconButton>
                    <Typography variant="body2" fontWeight={600}>{b.ordem}</Typography>
                    <IconButton size="small" disabled={idx === banners.length - 1} onClick={() => handleReorder(b.id, 'down')}><ArrowDownward fontSize="small" /></IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  {b.imagem ? (
                    <Box sx={{ width: 60, height: 36, borderRadius: 1, overflow: 'hidden', bgcolor: '#eee' }}>
                      <img src={b.imagem} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                  ) : (
                    <Box sx={{ width: 60, height: 36, borderRadius: 1, background: b.corFundo, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ImageIcon sx={{ color: 'white', fontSize: 18 }} />
                    </Box>
                  )}
                </TableCell>
                <TableCell><Typography variant="body2" fontWeight={600}>{b.titulo}</Typography></TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{b.subtitulo || '-'}</Typography></TableCell>
                <TableCell>
                  <Chip size="small" label={b.estado === 'A' ? 'Activo' : 'Inactivo'} color={b.estado === 'A' ? 'success' : 'default'} onClick={() => handleToggle(b)} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {b.dataInicio ? new Date(b.dataInicio).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' }) : 'Sempre'}
                    {b.dataFim ? ` — ${new Date(b.dataFim).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}` : ''}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar"><IconButton size="small" onClick={() => handleOpen(b)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => handleDelete(b.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Create Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Editar Banner' : 'Novo Banner'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Título" value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} size="small" required />
            <TextField label="Subtítulo" value={form.subtitulo} onChange={e => setForm({ ...form, subtitulo: e.target.value })} size="small" />

            {/* Image Upload */}
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>Imagem do Banner</Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button variant="outlined" component="label" startIcon={<ImageIcon />} size="small">
                  {form.imagem ? 'Alterar' : 'Upload'}
                  <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                </Button>
                {(form.imagem || (editing?.imagem)) && (
                  <Box sx={{ width: 120, height: 60, borderRadius: 1, overflow: 'hidden', border: '1px solid #eee' }}>
                    <img src={form.imagem || editing?.imagem || ''} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                )}
              </Box>
            </Box>

            {/* Gradient */}
            <TextField select label="Cor de Fundo (quando sem imagem)" value={form.corFundo} onChange={e => setForm({ ...form, corFundo: e.target.value })} size="small">
              {GRADIENT_PRESETS.map(g => (
                <MenuItem key={g.value} value={g.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 24, height: 16, borderRadius: 0.5, background: g.value }} />
                    {g.label}
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <TextField label="Link (URL)" value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} size="small" placeholder="https://..." />

            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField select label="Acção" value={form.acao} onChange={e => setForm({ ...form, acao: e.target.value })} size="small" fullWidth>
                  <MenuItem value="none">Nenhuma</MenuItem>
                  <MenuItem value="restaurant">Abrir Restaurante</MenuItem>
                  <MenuItem value="category">Abrir Categoria</MenuItem>
                  <MenuItem value="external">Link Externo</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField label="ID da Acção" value={form.acaoId} onChange={e => setForm({ ...form, acaoId: e.target.value })} size="small" fullWidth placeholder="ID do restaurante/categoria" />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField label="Data Início" type="datetime-local" value={form.dataInicio} onChange={e => setForm({ ...form, dataInicio: e.target.value })} size="small" fullWidth InputLabelProps={{ shrink: true }} helperText="Vazio = sempre" />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField label="Data Fim" type="datetime-local" value={form.dataFim} onChange={e => setForm({ ...form, dataFim: e.target.value })} size="small" fullWidth InputLabelProps={{ shrink: true }} helperText="Vazio = sem limite" />
              </Grid>
            </Grid>

            <FormControlLabel control={<Switch checked={form.estado === 'A'} onChange={e => setForm({ ...form, estado: e.target.checked ? 'A' : 'I' })} />} label="Activo" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'A guardar...' : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      {toast.open && (
        <Alert severity={toast.sev} sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999 }} onClose={() => setToast({ ...toast, open: false })}>
          {toast.msg}
        </Alert>
      )}
    </Box>
  );
};

export default FoodBanners;
